import { useQuery } from '@tanstack/react-query';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useIsPreActiveCustomer } from '../../../../../../../hooks/useIsPreActiveCustomer';
import { getDemoIncomeStatement } from '../demoAccountingData';

export function useIncomeStatement(
  customerId: string,
  year: number,
  compareTo?: 'previous'
) {
  const { callProtectedApi } = useApi();
  const isPreActive = useIsPreActiveCustomer();

  return useQuery({
    queryKey: ['accounting-income-statement', customerId, year, compareTo],
    queryFn: async () => {
      const params = new URLSearchParams({
        startDate: `${year}-01-01`,
        endDate: `${year + 1}-01-01`,
      });
      if (compareTo) params.set('compareTo', compareTo);

      const response = await callProtectedApi(
        `/accounting/${customerId}/income-statement?${params.toString()}`,
        'get'
      );
      return response?.data?.data;
    },
    // Pre-active customers see demo data, like every other Finances tab.
    // `enabled: false` is what stops the request — the accounting endpoints are
    // active-only and answer a pre-active customer with a 403 (Sentry
    // NODE-AWSLAMBDA-YD / -YE).
    //
    // `placeholderData`, not `initialData`: placeholder data is handed to the
    // observer but never written to the query cache, so fabricated figures can
    // never be served back as if they had been fetched. Everywhere else in the
    // app (MonthlyBudget, FinancialOverview) demo data is substituted at the
    // consumer for the same reason — it never touches the cache. It still
    // renders the card populated on the first paint, so demo data never flashes
    // a loading state.
    enabled: !!customerId && !isPreActive,
    placeholderData: isPreActive
      ? () => getDemoIncomeStatement(year, compareTo)
      : undefined,
  });
}
